"use client";

import { useState } from "react";
import { AppStoreCTA } from "@/app/components/AppStoreCTA";
import type { PatternQuizData } from "@/content/pseo/types";

function getArchetype(
  data: PatternQuizData,
  answers: number[],
): PatternQuizData["archetypes"][number] {
  const q1 = answers[0];
  const q4 = answers[3];

  const byKey = (key: string) =>
    data.archetypes.find((a) => a.key === key) ?? data.archetypes[0];

  if (q1 === 0) return byKey(data.archetypes[0]?.key ?? "");
  if (q1 === 1) return byKey(data.archetypes[1]?.key ?? "");
  if (q1 === 2) return byKey(data.archetypes[2]?.key ?? "");
  if (q4 === 0) return byKey(data.archetypes[0]?.key ?? "");
  return byKey(data.archetypes[3]?.key ?? "");
}

export function PatternQuiz({ data }: { data: PatternQuizData }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);

  const numQuestions = data.questions.length;
  const showResult = answers.length === numQuestions;

  function handleAnswer(optionIndex: number) {
    const newAnswers = [...answers, optionIndex];
    setAnswers(newAnswers);
    if (newAnswers.length < numQuestions) {
      setCurrentQ(currentQ + 1);
    }
  }

  function retake() {
    setAnswers([]);
    setCurrentQ(0);
  }

  const archetype = showResult ? getArchetype(data, answers) : null;

  return (
    <section className="mb-16">
      <p
        className="font-nunito font-semibold text-xs uppercase tracking-widest mb-5"
        style={{ color: "#3D5225", letterSpacing: "0.12em" }}
      >
        Quiz
      </p>
      <h2
        className="font-fraunces font-bold text-3xl mb-8"
        style={{ color: "#1C1917" }}
      >
        {data.promptHeading}
      </h2>

      <div
        className="rounded-2xl border p-8"
        style={{
          backgroundColor: "rgba(255,255,255,0.75)",
          borderColor: "rgba(90,122,56,0.25)",
        }}
      >
        {!showResult ? (
          <>
            <div className="flex items-center gap-3 mb-6">
              <p className="font-nunito text-xs" style={{ color: "#57534E" }}>
                Question {currentQ + 1} of {numQuestions}
              </p>
              <div
                className="flex-1 rounded-full overflow-hidden"
                style={{ height: "3px", backgroundColor: "rgba(90,122,56,0.15)" }}
              >
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${(currentQ / numQuestions) * 100}%`,
                    backgroundColor: "#5A7A38",
                  }}
                />
              </div>
            </div>

            <p
              className="font-fraunces font-semibold text-xl mb-6"
              style={{ color: "#1C1917" }}
            >
              {data.questions[currentQ].text}
            </p>

            <div className="flex flex-col gap-3">
              {data.questions[currentQ].options.map((option, i) => (
                <button
                  key={i}
                  onClick={() => handleAnswer(i)}
                  className="w-full text-left rounded-xl px-5 py-4 font-nunito text-base border transition-all duration-150 hover:border-[#5A7A38] hover:bg-white active:scale-[0.99]"
                  style={{
                    borderColor: "rgba(90,122,56,0.2)",
                    color: "#1C1917",
                    backgroundColor: "rgba(255,255,255,0.5)",
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div
              className="inline-block rounded-full px-4 py-1.5 mb-5 font-nunito text-xs font-semibold uppercase"
              style={{
                backgroundColor: "rgba(90,122,56,0.1)",
                color: "#3D5225",
                letterSpacing: "0.1em",
              }}
            >
              Your pattern
            </div>

            <h3
              className="font-fraunces font-bold text-2xl mb-4"
              style={{ color: "#1C1917" }}
            >
              {archetype!.title}
            </h3>

            <p
              className="font-nunito text-base leading-relaxed mb-4"
              style={{ color: "#57534E" }}
            >
              {archetype!.description}
            </p>

            <p
              className="font-nunito text-base leading-relaxed mb-8"
              style={{ color: "#3D5225" }}
            >
              {archetype!.bridge}
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <AppStoreCTA size="medium" />
              <button
                onClick={retake}
                className="font-nunito text-sm underline underline-offset-2 transition-opacity hover:opacity-60"
                style={{ color: "#57534E" }}
              >
                Retake quiz
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
